import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { visibleInterval } from '@/lib/polling';
import { INVALIDATE_DEBOUNCE_MS, useRealtime, useRealtimeConnected } from '@/lib/realtime';
import type { CalendarDay, CalendarSnapshot } from './calendar-types';
import { nowMinute } from './calendar-time';
import { concernsCalendar } from './calendar-events';

export const CALENDAR_STALE_MS = 10_000;
const CONNECTED_REFETCH_MS = 300_000;
const DISCONNECTED_REFETCH_MS = 60_000;
const NOW_TICK_MS = 60_000;

export function calendarKey(branchId: string, departmentId: string, from: string, days: number): QueryKey {
  return ['calendar', branchId, departmentId, from, days];
}

function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

export function useCalendarSnapshot(branchId: string, departmentId: string, from: string, days: number) {
  const queryClient = useQueryClient();
  const connected = useRealtimeConnected();
  const key = calendarKey(branchId, departmentId, from, days);

  const query = useQuery({
    queryKey: key,
    queryFn: () => get<CalendarSnapshot>(`/appointments/calendar${qs({ branchId, departmentId: departmentId || undefined, from, days })}`),
    enabled: Boolean(branchId && from),
    staleTime: CALENDAR_STALE_MS,
    refetchInterval: visibleInterval(connected ? CONNECTED_REFETCH_MS : DISCONNECTED_REFETCH_MS),
    refetchOnWindowFocus: (cached) => cached.state.isInvalidated,
  });

  const providerIds = useRef<Set<string>>(new Set());
  providerIds.current = new Set(query.data?.providers.map((provider) => provider.id) ?? []);
  const keyRef = useRef(key);
  keyRef.current = key;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useRealtime(branchId ? [`queue-branch:${branchId}`] : [], (event, payload) => {
    if (!concernsCalendar(event, payload, providerIds.current)) return;
    if (timer.current) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      void queryClient.invalidateQueries({ queryKey: keyRef.current, refetchType: pageVisible() ? 'active' : 'none' });
    }, INVALIDATE_DEBOUNCE_MS);
  });

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return { query, connected, key };
}

export function useNowMinute(days: CalendarDay[] | undefined): { dayIndex: number; minute: number } | null {
  const [tick, setTick] = useState(() => Date.now());

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (interval) return;
      setTick(Date.now());
      interval = setInterval(() => setTick(Date.now()), NOW_TICK_MS);
    };
    const stop = () => {
      if (interval) clearInterval(interval);
      interval = null;
    };
    const onVisibility = () => (document.visibilityState === 'visible' ? start() : stop());
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return useMemo(() => (days && days.length > 0 ? nowMinute(days, new Date(tick).toISOString()) : null), [days, tick]);
}
